import { FC } from "react";
import { components } from "react-select";
import styled, { css } from "styled-components";
import { getTextVariant } from "./getTextVariant";
import { styleWrappedComponent } from "./styleWrappedComponent";
import { inputTypography } from "./inputTypography";
import { InputVariant, SelectComponentProps } from "./types";
import { useDropdownContext } from "./DropdownContext";
import { SizeVariant } from "../types";

type getControlBorderColorArgs = {
  menuIsOpen: boolean;
  isFocused: boolean;
  isDisabled: boolean;
  hasError: boolean;
};

const getControlBorderColor = ({
  menuIsOpen,
  isFocused,
  isDisabled,
  hasError,
}: getControlBorderColorArgs) => {
  if (hasError) {
    return "var(--color-error-500)";
  }
  if (isDisabled) {
    return "var(--color-primary-200)";
  }
  if (menuIsOpen || isFocused) {
    return "var(--color-secondary-800)";
  }
  return "var(--color-primary-400)";
};

const getControlHoverBorderColor = ({
  menuIsOpen,
  isFocused,
  isDisabled,
  hasError,
}: getControlBorderColorArgs) => {
  if (hasError) {
    return "var(--color-error-500)";
  }
  if (isDisabled) {
    return "var(--color-primary-200)";
  }
  if (menuIsOpen || isFocused) {
    return "var(--color-secondary-800)";
  }
  return "var(--color-primary-800)";
};

type StyledControlProps = {
  variant: InputVariant;
  size: SizeVariant;
  menuIsOpen: boolean;
  isFocused: boolean;
  isDisabled: boolean;
  hasError: boolean;
};
const StyledControl = styled.div<StyledControlProps>`
  cursor: pointer;
  ${styleWrappedComponent(css<StyledControlProps>`
    cursor: pointer;
    background-color: var(--color-neutral-000-full);
    border-color: ${(props) => getControlBorderColor(props)};
    border-radius: var(--size-border-radius-small);
    box-shadow: none;
    min-height: ${(props) => (props.size === "SMALL" ? "24px" : "32px")};
    :hover {
      border-color: ${(props) => getControlHoverBorderColor(props)};
    }
  `)}
  ${(props) => inputTypography(props.variant)};
`;

type ControlProps = SelectComponentProps["Control"];

export const Control: FC<ControlProps> = ({ children, ...props }) => {
  const { hasError } = useDropdownContext();
  return (
    <StyledControl
      variant={getTextVariant(props)}
      size={props.selectProps.size}
      menuIsOpen={props.menuIsOpen}
      isFocused={props.isFocused}
      isDisabled={props.isDisabled}
      hasError={hasError}
    >
      <components.Control {...props}>{children}</components.Control>
    </StyledControl>
  );
};
